import React, { useState, useContext } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useEffectOnce, useToggle } from 'react-use';
import styled from 'styled-components';
import { isEmpty } from 'lodash';
import axios from 'axios';
import { Form } from 'react-final-form';
import {
  Container,
  Box,
  Row,
  Card,
  Flex,
  Button,
  Text,
} from '../../design-system';
import dayjs from '../../helpers/dates';
import { AutosaveEffect } from '../../hooks/useAutosave';
import useSaveAction from '../../hooks/useSaveAction';
import Alert from '../../helpers/alert';
import {
  useData,
  successRefetch,
  isAccessDenied,
} from '../../helpers/data-loader';
import {
  prettyPhone,
  currencyFormatter,
  formatEIN,
  checkPermissions,
} from '../../helpers/utils';
import Actions from '../../components/Actions';
import Attachments from '../../components/Attachments';
import Chart from '../../components/Chart';
import FieldColumn from '../../components/FieldColumn';
import ModalDelete from '../../components/ModalDelete';
import NotAccessible from '../../components/NotAccessible';
import ReadOnlyColumn from '../../components/ReadOnlyColumn';
import YearToDateCard from '../../components/YearToDateCard';
import {
  PERMISSIONS,
  ERR_MSG,
  COMPLETED_LIST,
  STATE_OPTIONS,
  COUNTRY_OPTIONS,
  US_STATES,
} from '../../globalConstants';
import { Session } from '../../contexts/sessionContext';
import { EDIT_FIELDS } from './constants';
import CardHeader from '../../components/CardHeader';
import ContactList from '../../components/ContactList';
import AddProjectModal from '../../components/AddProjectModal';

const StyledCard = styled(Card)`
  box-shadow: 0px 0px 10px #d7d7d7;
`;

const ClientEdit = () => {
  const {
    state: {
      user: { permissions, isClient },
    },
  } = useContext(Session);
  const navigate = useNavigate();
  const { id } = useParams();
  const [isClientDeleteModalOpen, toggleClientDeleteModal] = useToggle(false);
  const canEdit = checkPermissions(permissions, [PERMISSIONS.UPDATE_CLIENTS]);

  const [actionsModal, toggleActionsModal] = useState(false);
  const [attachmentsModal, toggleAttachmentsModal] = useState(false);
  const [lastSave, setLastSave] = useState<any>(null);
  const [isEditing, setEditingStatus] = useState(false);
  const [client, fetchClient, clientLoading, error] = useData(`/client/${id}`, {
    errorMsg: ERR_MSG,
  });
  const { phone, fax, current_budget, tax_id } = client;
  const modelId = id;
  const modelType = 'AppModelsClient';

  useEffectOnce(() => {
    fetchClient();
  });

  const toggleEditingStatus = () => {
    setEditingStatus(!isEditing);
  };

  const handleDeleteClient = async () => {
    try {
      await axios.delete(`/client/${client.id}`);
      toggleClientDeleteModal();
      navigate('/clients', { replace: true });
    } catch {
      Alert.error(ERR_MSG);
    }
  };

  const [onSubmit, saving] = useSaveAction(async (values: any) => {
    await axios.put(`/client/${client.id}`, {
      ...values,
      current_budget:
        values.current_budget && values.current_budget.replace(/[^0-9.]/g, ''),
    });
    setLastSave(dayjs());
  });

  const finishEditing = () => {
    toggleEditingStatus();
    successRefetch(fetchClient);
  };

  if (isAccessDenied(error)) {
    return (
      <NotAccessible
        text="You don't have access to this client."
        redirect="/clients"
      />
    );
  }

  if (error && error.code === 405) {
    return <NotAccessible text="You are not permitted to view this page." />;
  }

  return (
    <Container pb={4} fluid>
      <Flex my={20} justifyContent="space-between" alignItems="center">
        <Text id="client-page-title" fontSize={25} fontWeight={600}>
          {client?.name}
        </Text>
        <AddProjectModal
          {...({ initialValues: { client: parseInt(id, 10) } } as any)}
        />
      </Flex>
      {!isEmpty(client) && !clientLoading && (
        <>
          <Flex>
            <YearToDateCard
              height={325}
              data={client.projects}
              activeSection={{
                title: 'Active Project',
                value: client.projects.filter(
                  (project) =>
                    COMPLETED_LIST.indexOf(project.status) === -1 && project,
                ).length,
              }}
            />
            <Box ml={4} flex={1}>
              <StyledCard p={2} height={325}>
                <Chart data={client.projects} />
              </StyledCard>
            </Box>
          </Flex>
          <Form
            onSubmit={onSubmit}
            initialValues={{
              country: 'United States',
              ...client,
              phone: phone && prettyPhone(phone),
              fax: fax && prettyPhone(fax),
              current_budget:
                current_budget && currencyFormatter.format(current_budget),
              state:
                US_STATES.find(
                  ({ abbreviation }) => abbreviation === client.state,
                )?.name || client.state,
              tax_id: tax_id && formatEIN(tax_id),
            }}
            render={({ handleSubmit, values }) => (
              <form onSubmit={handleSubmit}>
                <Card mt={30}>
                  <CardHeader
                    title={client.fullName}
                    updatedAt={lastSave || client.updated_at}
                    saving={saving}
                    isSmall
                  />
                  <Card.Body>
                    <Row style={{ overflowWrap: 'break-word' }} mt={20}>
                      {EDIT_FIELDS.map((field) =>
                        canEdit && isEditing ? (
                          <FieldColumn
                            id={`${field.label.replace(/ /g, '')}-edit-field`}
                            key={field.name}
                            width={300}
                            {...field}
                            {...(field.name === 'state' && {
                              options: values.country
                                ? STATE_OPTIONS(values.country)
                                : [],
                            })}
                            {...(field.name === 'country' && {
                              options: COUNTRY_OPTIONS,
                            })}
                          />
                        ) : (
                          <ReadOnlyColumn
                            key={field.name}
                            data={client}
                            field={field}
                          />
                        ),
                      )}
                    </Row>
                    {canEdit &&
                      (isEditing ? (
                        <>
                          <AutosaveEffect
                            data={values}
                            onSave={handleSubmit}
                            interval={200}
                          />
                          <Flex mt={30} justifyContent="flex-end">
                            <Button
                              id="exit-edit-button"
                              title="Update"
                              aria-label="Finish Editing Client"
                              loading={saving}
                              onClick={finishEditing}
                            >
                              Finish Editing
                            </Button>
                          </Flex>
                        </>
                      ) : (
                        <Flex mt={30} justifyContent="flex-end">
                          <Button
                            id="enter-edit-button"
                            title="Edit"
                            aria-label="Edit Client"
                            type="button"
                            onClick={toggleEditingStatus}
                          >
                            Edit
                          </Button>
                          {!isClient && (
                            <Button
                              id="delete-client-button"
                              ml={20}
                              aria-label="Delete Client"
                              type="button"
                              variant="primaryText"
                              onClick={toggleClientDeleteModal}
                            >
                              Delete
                            </Button>
                          )}
                        </Flex>
                      ))}
                  </Card.Body>
                </Card>
              </form>
            )}
          />
          <Actions
            {...({
              data: client.actions || [],
              isModalOpen: actionsModal,
              modelId,
              modelType,
              onClick: () => toggleActionsModal(true),
              onClose: () => toggleActionsModal(false),
              refetch: fetchClient,
              maxHeight: '25rem',
              canEdit,
            } as any)}
          />

          <ContactList
            modelType="client"
            modelID={client.id}
            assignedContacts={client.assignedContacts || []}
            allContacts={client.allContacts || []}
            refetch={fetchClient}
            canEdit={canEdit}
          />

          <Attachments
            data={client.files || []}
            canEdit={canEdit}
            modelId={modelId}
            modelType={modelType}
            clientId={id}
            refetch={fetchClient}
            maxHeight="25rem"
            disabled={!canEdit}
          />
        </>
      )}
      <ModalDelete
        entity="client"
        open={isClientDeleteModalOpen}
        onCancel={toggleClientDeleteModal}
        onDelete={handleDeleteClient}
      />
    </Container>
  );
};

export default ClientEdit;
